import { MIN_PASSWORD_LENGTH, passwordProblems } from '@wms/domain';
import { useEffect, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../api';
import { forgetSavedSession } from '../session';
import { Icon } from '../ui/icons';
import { LogoMark } from '../ui/Logo';

/**
 * The page the "Şifremi unuttum" e-mail links to (#/sifre-yenile/<token>): a new password, set once.
 * Shown whether or not someone is signed in on this browser; afterwards everyone signs in again.
 */
export function ResetPassword({ token }: { token: string }) {
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  // a link that no longer works says so before a new password is typed
  const [dead, setDead] = useState<string | null>(null);
  useEffect(() => {
    api('POST', '/api/auth/reset/check', { token }).catch((e: unknown) => setDead(errorMessage(e)));
  }, [token]);
  const problems = next ? passwordProblems(next) : [];
  const mismatch = again.length > 0 && again !== next;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (problems.length > 0 || mismatch || !again) return;
    setBusy(true);
    setError(null);
    try {
      await api('POST', '/api/auth/reset', { token, password: next });
      setDone(true);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  // a fresh start at the login screen, also on a shared computer where someone else was signed in
  const toLogin = () => {
    forgetSavedSession();
    window.location.replace(`${window.location.pathname}#/`);
  };

  return (
    <div className="login">
      <div className="card login-card">
        <div className="login-card-h">
          <LogoMark size={40} />
          <div>
            <h2>Yeni şifre belirleyin</h2>
            <span className="muted">Pronto Depo</span>
          </div>
        </div>
        {dead ? (
          <div className="card-b stack">
            <div className="banner bad">{dead}</div>
            <button className="btn primary big block" onClick={toLogin}>
              Giriş ekranına dön
            </button>
          </div>
        ) : done ? (
          <div className="card-b stack">
            <div className="banner ok">
              <Icon name="check" />
              <div>Şifreniz değişti. Açık oturumlarınız kapatıldı; yeni şifrenizle giriş yapın.</div>
            </div>
            <button className="btn primary big block" onClick={toLogin}>
              Giriş ekranına git
            </button>
          </div>
        ) : (
          <form className="card-b stack" onSubmit={(e) => void submit(e)}>
            {error && <div className="banner bad">{error}</div>}
            <p className="muted" style={{ margin: 0 }}>
              Bu bağlantı 30 dakika geçerlidir ve bir kez çalışır. Şifreniz değişince bu hesapla açık tüm oturumlar kapanır.
            </p>
            <div className="field">
              <label htmlFor="reset-next">Yeni şifre</label>
              <input id="reset-next" className="input" type="password" autoComplete="new-password" autoFocus value={next} onChange={(e) => setNext(e.target.value)} required />
              <span className={`hint${problems.length > 0 ? ' bad' : ''}`}>
                {problems.length > 0 ? problems.join(' · ') : `En az ${MIN_PASSWORD_LENGTH} karakter, harf ve rakam; e-posta adınızı içermesin`}
              </span>
            </div>
            <div className="field">
              <label htmlFor="reset-again">Yeni şifre (tekrar)</label>
              <input id="reset-again" className="input" type="password" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} required />
              {mismatch && <span className="hint bad">Şifreler aynı değil</span>}
            </div>
            <button className="btn primary big block" disabled={busy || problems.length > 0 || mismatch || !next || !again}>
              {busy ? 'Kaydediliyor…' : 'Şifremi kaydet'}
            </button>
            <button type="button" className="btn ghost sm" style={{ alignSelf: 'center' }} onClick={toLogin}>
              Giriş ekranına dön
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
